import React, { useState, useRef, useCallback } from 'react';
import {
  PenSquare, Search, X, ImagePlus, Trash2, Loader2, CheckCircle, AlertCircle, User,
} from 'lucide-react';
import { searchUsers, uploadPostMedia, createPost, UserSearchResult } from '../services/api.ts';
import './CreatePost.css';

const CreatePost: React.FC = () => {
  /* ── user search ── */
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<UserSearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserSearchResult | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleSearch = useCallback((value: string) => {
    setQuery(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (value.length < 2) { setSuggestions([]); return; }
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const data = await searchUsers(value);
        setSuggestions(data);
      } catch {
        setSuggestions([]);
      } finally {
        setSearching(false);
      }
    }, 300);
  }, []);

  const pickUser = (u: UserSearchResult) => {
    setSelectedUser(u);
    setQuery('');
    setSuggestions([]);
  };

  const clearUser = () => {
    setSelectedUser(null);
  };

  /* ── post content ── */
  const [content, setContent] = useState('');

  /* ── images ── */
  const [imageFiles, setImageFiles] = useState<File[]>([]);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const addImages = (files: FileList | null) => {
    if (!files) return;
    const newFiles = Array.from(files).filter(f => f.type.startsWith('image/'));
    const combined = [...imageFiles, ...newFiles].slice(0, 10); // max 10 images
    setImageFiles(combined);

    // Generate previews
    const previews = combined.map(f => URL.createObjectURL(f));
    // Revoke old previews to avoid memory leaks
    imagePreviews.forEach(p => URL.revokeObjectURL(p));
    setImagePreviews(previews);
  };

  const removeImage = (index: number) => {
    const newFiles = imageFiles.filter((_, i) => i !== index);
    setImageFiles(newFiles);
    URL.revokeObjectURL(imagePreviews[index]);
    setImagePreviews(imagePreviews.filter((_, i) => i !== index));
  };

  /* ── submit ── */
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const canSubmit = selectedUser && content.trim().length > 0 && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit || !selectedUser) return;
    setSubmitting(true);
    setError(null);
    setSuccess(null);

    try {
      // 1. Upload images if any
      let mediaUrls: string[] = [];
      if (imageFiles.length > 0) {
        mediaUrls = await uploadPostMedia(selectedUser.user_id, imageFiles);
      }

      // 2. Create the post
      const result = await createPost(selectedUser.user_id, content.trim(), mediaUrls);
      setSuccess(`Post #${result.post_id} created successfully as ${selectedUser.name}`);

      // Reset form
      setContent('');
      setImageFiles([]);
      imagePreviews.forEach(p => URL.revokeObjectURL(p));
      setImagePreviews([]);
      setSelectedUser(null);
    } catch (e: any) {
      const detail = e?.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d: any) => d.msg || JSON.stringify(d)).join('; ')
        : typeof detail === 'string'
          ? detail
          : e.message || 'Failed to create post';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="create-post">
      <div className="cp-header">
        <h2><PenSquare size={22} /> Create Post</h2>
        <span className="cp-subtitle">Post on behalf of a user account</span>
      </div>

      {success && (
        <div className="cp-alert cp-alert-success">
          <CheckCircle size={18} /> {success}
        </div>
      )}
      {error && (
        <div className="cp-alert cp-alert-error">
          <AlertCircle size={18} /> {error}
        </div>
      )}

      <div className="cp-form">
        {/* ── User Selector ── */}
        <div className="cp-section">
          <label className="cp-label">Post as User</label>

          {selectedUser ? (
            <div className="cp-selected-user">
              <div className="cp-user-avatar">
                {selectedUser.avater ? (
                  <img src={selectedUser.avater} alt="" onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                ) : (
                  <User size={20} />
                )}
              </div>
              <div className="cp-user-info">
                <span className="cp-user-name">{selectedUser.name}</span>
                <span className="cp-user-email">{selectedUser.email || selectedUser.user_id}</span>
              </div>
              <button className="cp-user-clear" onClick={clearUser} type="button">
                <X size={16} />
              </button>
            </div>
          ) : (
            <div className="cp-search-wrap">
              <Search size={16} className="cp-search-icon" />
              <input
                type="text"
                className="cp-search-input"
                placeholder="Search user by name, email, or ID…"
                value={query}
                onChange={e => handleSearch(e.target.value)}
                autoComplete="off"
              />
              {searching && <Loader2 size={16} className="cp-search-spin" />}

              {suggestions.length > 0 && (
                <ul className="cp-suggestions">
                  {suggestions.map(u => (
                    <li key={u.user_id} onClick={() => pickUser(u)}>
                      <div className="cp-sug-avatar">
                        {u.avater ? (
                          <img src={u.avater} alt="" onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                        ) : (
                          <User size={16} />
                        )}
                      </div>
                      <div className="cp-sug-info">
                        <span className="cp-sug-name">{u.name}</span>
                        <span className="cp-sug-email">{u.email || u.user_id}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>

        {/* ── Post Content ── */}
        <div className="cp-section">
          <label className="cp-label">Content</label>
          <textarea
            className="cp-textarea"
            placeholder="Write the post content…"
            value={content}
            onChange={e => setContent(e.target.value)}
            rows={6}
          />
          <span className="cp-char-count">{content.length} characters</span>
        </div>

        {/* ── Image Upload ── */}
        <div className="cp-section">
          <label className="cp-label">Images <span className="cp-optional">(optional, max 10)</span></label>

          <div className="cp-image-grid">
            {imagePreviews.map((src, i) => (
              <div key={i} className="cp-image-thumb">
                <img src={src} alt={`Upload ${i + 1}`} />
                <button className="cp-image-remove" onClick={() => removeImage(i)} type="button">
                  <Trash2 size={14} />
                </button>
              </div>
            ))}

            {imageFiles.length < 10 && (
              <button
                className="cp-image-add"
                onClick={() => fileInputRef.current?.click()}
                type="button"
              >
                <ImagePlus size={24} />
                <span>Add Image</span>
              </button>
            )}
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            hidden
            onChange={e => { addImages(e.target.files); e.target.value = ''; }}
          />
        </div>

        {/* ── Submit ── */}
        <div className="cp-actions">
          <button
            className="cp-submit"
            disabled={!canSubmit}
            onClick={handleSubmit}
          >
            {submitting ? (
              <><Loader2 size={18} className="cp-search-spin" /> Creating…</>
            ) : (
              <><PenSquare size={18} /> Create Post</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreatePost;
